import React, { useState, useRef, useEffect } from 'react';
import { LayoutDashboard, Users, UserPlus, LogOut, Bell, X, Cpu, KeyRound } from 'lucide-react';

import Logo from '../../assets/Mask_group.png';
import { Link, Outlet, useLocation } from 'react-router-dom';
import KpmgLogo from '../../assets/KPMG_white_logo.svg'
import Profile from '../../assets/user1.png';
import API, { clearClientSession } from '../../services/api.js';
import { useNavigate } from 'react-router-dom';
import LoadingPopup from '../misc/Loading.js';
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from "react-icons/md";
import { usePolling } from '../../services/PollingContext.js';

const SuperAdminDashboard = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const location = useLocation();
  const path = location.pathname;
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navigate = useNavigate();
  const { notifications, dismissNotification, markAllAsRead, getUnreadCount } = usePolling();

  const handleViewClick = (tenantName) => {
    navigate(`/admin-dashboard/tenants?tenant_name=${encodeURIComponent(tenantName)}`);
  };

  const [fetchedadmindashboard, setFetchedAdminDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  const fetchAdminDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/admin-dashboard/`);
      setFetchedAdminDashboard(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch dashboard data");
      console.error("Error fetching JSON:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminDashboard();
  }, []);

  const scanCount = fetchedadmindashboard?.scan_count || 0;
  const tenantCount = fetchedadmindashboard?.tenant_count || 0;
  const userCount = fetchedadmindashboard?.user_count || 0;
  const assetCount = fetchedadmindashboard?.asset_count || 0;
  const tenantDetails = fetchedadmindashboard?.tenant_details || [];

  const handleLogout = async () => {
    if (!await clearClientSession()) return;
    window.location.href = '/';
  };

  const metricColors = [
    { bg: 'bg-gradient-to-br from-blue-600/20 to-blue-800/20', border: 'border-blue-500/30', accent: 'text-blue-400' },
    { bg: 'bg-gradient-to-br from-emerald-600/20 to-emerald-800/20', border: 'border-emerald-500/30', accent: 'text-emerald-400' },
    { bg: 'bg-gradient-to-br from-amber-600/20 to-amber-800/20', border: 'border-amber-500/30', accent: 'text-amber-400' },
    { bg: 'bg-gradient-to-br from-red-600/20 to-red-800/20', border: 'border-red-500/30', accent: 'text-red-400' },
  ];

  const renderContent = () => {
    if (path === '/admin-dashboard') {
      return (
        <div className="text-white p-4 m-12">
          <div className="grid grid-cols-2 gap-6">
            <div className={`border ${metricColors[0].border} rounded-xl ${metricColors[0].bg} backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10`}>
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Total Tenants</p>
              </div>
              <div>
                <p className={`font-bold text-5xl ml-7 m-2 mb-7 ${metricColors[0].accent}`}>{tenantCount}</p>
              </div>
            </div>

            <div className={`border ${metricColors[1].border} rounded-xl ${metricColors[1].bg} backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/10`}>
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Total Users</p>
              </div>
              <div>
                <p className={`font-bold text-5xl ml-7 m-2 mb-7 ${metricColors[1].accent}`}>{userCount}</p>
              </div>
            </div>

            <div className={`border ${metricColors[2].border} rounded-xl ${metricColors[2].bg} backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/10`}>
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Total Assets</p>
              </div>
              <div>
                <p className={`font-bold text-5xl ml-7 m-2 mb-7 ${metricColors[2].accent}`}>{assetCount}</p>
              </div>
            </div>

            <div className={`border ${metricColors[3].border} rounded-xl ${metricColors[3].bg} backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-500/10`}>
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Total Scans</p>
              </div>
              <div>
                <p className={`font-bold text-5xl ml-7 m-2 mb-7 ${metricColors[3].accent}`}>{scanCount}</p>
              </div>
            </div>
          </div>

          <div className="mt-12 border border-[#232325] rounded-xl overflow-hidden">
            <table className="min-w-full divide-y divide-[#232325]">
              <thead className="bg-[#121212] text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-md font-medium">S.No</th>
                  <th className="px-6 py-3 text-center text-md font-medium">Tenant Name</th>
                  <th className="px-6 py-3 text-center text-md font-medium">User Count</th>
                  <th className="px-6 py-3 text-center text-md font-medium">Application Count</th>
                  <th className="px-6 py-3 text-right text-md font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#232325] bg-[#0D0D0E]">
                {tenantDetails && tenantDetails.length > 0 ? (
                  tenantDetails.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-4 text-md">{idx + 1}</td>
                      <td className="px-6 py-4 text-center text-md">{item.tenant_name}</td>
                      <td className="px-6 py-4 text-center text-md">{item.user_count}</td>
                      <td className="px-6 py-4 text-center text-md">{item.asset_count}</td>
                      <td className="px-6 py-4 text-md text-right">
                        <button
                          onClick={() => handleViewClick(item.tenant_name)}
                          className="bg-[#232325] text-white py-1 px-3 rounded-xl hover:bg-[#1c1c1e] transition cursor-pointer"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-sm text-center text-white">
                      No tenants found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return <Outlet />;
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setShowNotifications(false);
    setShowDropdown(false);
  };

  const getNotificationIconAndColor = (type, source) => {
    if (source === "System Prompt Hardening") {
      if (type === "success") return { Icon: Bell, color: "text-green-400", bgColor: "bg-green-400/10" };
      if (type === "error") return { Icon: Bell, color: "text-red-400", bgColor: "bg-red-400/10" };
      return { Icon: Bell, color: "text-blue-400", bgColor: "bg-blue-400/10" };
    }
    if (type === "scan") return { Icon: Bell, color: "text-blue-400", bgColor: "bg-blue-400/10" };
    if (type === "completed") return { Icon: Bell, color: "text-green-400", bgColor: "bg-green-400/10" };
    if (type === "alert") return { Icon: Bell, color: "text-red-400", bgColor: "bg-red-400/10" };
    if (type === "pending") return { Icon: Bell, color: "text-yellow-400", bgColor: "bg-yellow-400/10" };
    return { Icon: Bell, color: "text-gray-400", bgColor: "bg-gray-400/10" };
  };

  const getBellIconColor = () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length > 0) {
      const latestNotification = unreadNotifications[0];
      if (latestNotification.source === "System Prompt Hardening") {
        if (latestNotification.type === "success") return "text-green-400";
        if (latestNotification.type === "error") return "text-red-400";
        return "text-blue-400";
      }
      if (latestNotification.type === "completed") return "text-green-400";
      if (latestNotification.type === "alert") return "text-red-400";
      if (latestNotification.type === "scan") return "text-blue-400";
      if (latestNotification.type === "pending") return "text-yellow-400";
    }
    return "text-gray-400";
  };

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    setShowDropdown(false);
    if (!showNotifications) markAllAsRead();
  };

  const activePath = path.split('/').pop();

  return (
    <div className='h-screen w-full flex flex-row bg-[#0D0D0E] overflow-hidden relative'>
      {loading && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm">
          <LoadingPopup isVisible={true} />
        </div>
      )}

      {/* SIDEBAR */}
      <div className={`h-full bg-[#101012] border-r border-[#232325] transition-[width] duration-300 ease-in-out flex flex-col ${isCollapsed ? 'w-[4vw]' : 'w-[18vw]'}`}>
        {/* Header Section */}
        <div className={`flex flex-col ${isCollapsed ? 'items-center' : 'px-4'} pt-4 pb-6 border-b border-[#232325]`}>
          <div className={`flex ${isCollapsed ? 'flex-col items-center' : 'flex-row items-center justify-between'} w-full`}>
            <div className={`flex ${isCollapsed ? 'justify-center' : 'flex-row items-center space-x-1'} w-full`}>
              <div className={`${isCollapsed ? 'w-12 h-12' : 'w-16 h-16'} flex-shrink-0`}>
                <img src={Logo} alt='logo' className='w-full h-full object-contain aspect-square' />
              </div>
              {!isCollapsed && (
                <div className='flex flex-col items-start justify-end gap-1 m-2 mb-0 mt-5 ml-0'>
                  <img src={KpmgLogo} alt='KPMG' className='h-6 w-auto cursor-pointer'/>
                  <p className='text-sm text-white font-semibold mb-4 mt-0 cursor-pointer whitespace-nowrap tracking-tight'>AI Security Posture Scanner</p>
                </div>
              )}
            </div>

            {!isCollapsed && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
                className='p-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 bg-gray-800 border border-gray-600 ml-4 flex-shrink-0'
              >
                <MdKeyboardDoubleArrowLeft size={20} className='text-white' />
              </button>
            )}
          </div>

          {isCollapsed && (
            <div className='mt-4'>
              <button
                onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
                className='p-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 bg-gray-800 border border-gray-600'
              >
                <MdKeyboardDoubleArrowRight size={20} className='text-white' />
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className={`${isCollapsed ? 'px-2' : 'px-4'} py-6 flex-1 overflow-y-auto`}>
          <ul className={`flex flex-col ${isCollapsed ? 'items-center gap-4' : 'space-y-3'} text-white`}>
            <li className={`w-full rounded-xl flex flex-row items-center ${path === '/admin-dashboard' ? 'bg-blue-600/20 text-blue-400 border border-blue-400' : 'hover:bg-blue-600/20 hover:text-blue-400'}`}>
              <Link to={'/admin-dashboard'} className='w-full'>
                <div className='flex flex-row items-center w-full px-6 py-3 gap-3'>
                  <LayoutDashboard size={24} className='flex-shrink-0' />
                  {!isCollapsed && <p className='text-lg font-medium'>Dashboard</p>}
                </div>
              </Link>
            </li>
            <li className={`w-full rounded-xl flex flex-row items-center ${activePath === 'tenants' ? 'bg-blue-600/20 text-blue-400 border border-blue-400' : 'hover:bg-blue-600/20 hover:text-blue-400'}`}>
              <Link to={'/admin-dashboard/tenants'} className='w-full'>
                <div className='flex flex-row items-center w-full px-6 py-3 gap-3'>
                  <Users size={24} className='flex-shrink-0' />
                  {!isCollapsed && <p className='text-lg font-medium'>Tenants</p>}
                </div>
              </Link>
            </li>
            <li className={`w-full rounded-xl flex flex-row items-center ${activePath === 'onboarding' ? 'bg-blue-600/20 text-blue-400 border border-blue-400' : 'hover:bg-blue-600/20 hover:text-blue-400'}`}>
              <Link to={'/admin-dashboard/onboarding'} className='w-full'>
                <div className='flex flex-row items-center w-full px-6 py-3 gap-3'>
                  <UserPlus size={24} className='flex-shrink-0' />
                  {!isCollapsed && <p className='text-lg font-medium'>Onboarding</p>}
                </div>
              </Link>
            </li>
            <li className={`w-full rounded-xl flex flex-row items-center ${activePath === 'ai-providers' ? 'bg-blue-600/20 text-blue-400 border border-blue-400' : 'hover:bg-blue-600/20 hover:text-blue-400'}`}>
              <Link to={'/admin-dashboard/ai-providers'} className='w-full'>
                <div className='flex flex-row items-center w-full px-6 py-3 gap-3'>
                  <Cpu size={24} className='flex-shrink-0' />
                  {!isCollapsed && <p className='text-lg font-medium'>AI Providers</p>}
                </div>
              </Link>
            </li>
            <li className={`w-full rounded-xl flex flex-row items-center ${activePath === 'scan-access' ? 'bg-blue-600/20 text-blue-400 border border-blue-400' : 'hover:bg-blue-600/20 hover:text-blue-400'}`}>
              <Link to="/admin-dashboard/scan-access" className="w-full"><div className="flex flex-row items-center w-full px-6 py-3 gap-3"><KeyRound size={24} className="flex-shrink-0" />{!isCollapsed && <p className="text-lg font-medium">Scan API access</p>}</div></Link>
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className={`p-4 absolute bottom-0 w-auto ${isCollapsed ? 'flex justify-center' : ''}`}>
          <div className={`flex items-center justify-center ${isCollapsed ? 'flex-col space-y-2' : 'space-x-3'}`}>
            <img src={Profile} alt="profile" className="h-7 aspect-square" />
            {!isCollapsed && (
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-white text-md font-medium truncate">Super Admin</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className={`flex-1 flex flex-col overflow-y-auto pt-[10vh]`}>
        {/* Navbar */}
        <div
        className="h-[10vh] border-b border-[#232325] bg-[#0D0D0E] flex flex-row items-center justify-between fixed top-0 right-0 z-20 pr-6 transition-all duration-300"
        style={{
            marginLeft: isCollapsed ? '4vw' : '18vw',
            width: isCollapsed ? 'calc(100% - 4vw)' : 'calc(100% - 18vw)',
        }}
        >
        {/* Left side - Title */}
        <div className="flex items-center ml-12 gap-4">
            <span className="text-white font-medium text-lg">Super Admin Dashboard</span>
        </div>

        {/* Right side - Notifications & Profile */}
        <div className="flex items-center gap-4">
            {/* Notifications Button */}
            <div className="relative">
            <button
                onClick={handleNotificationClick}
                className="relative p-2 rounded-xl bg-[#1a1a1c] hover:bg-[#232325] border border-[#232325] transition-all"
            >
                <Bell size={20} className={`opacity-70 group-hover:opacity-100 transition-opacity ${getBellIconColor()}`} />
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-red-500 text-white text-xs rounded-full border-2 border-[#0D0D0E]">
                {getUnreadCount()}
                </span>
            </button>
            </div>

            {/* Profile Dropdown */}
            <div ref={dropdownRef} className="relative">
            <button
                onClick={() => setShowDropdown((prev) => !prev)}
                className="flex items-center gap-3 p-2 rounded-xl bg-[#1a1a1c] hover:bg-[#232325] border border-[#232325] transition-all"
            >
                <img src={Profile} alt="profile" className="h-5 w-5" />
                <span className="hidden md:block text-gray-400 group-hover:text-white">Super Admin</span>
            </button>
            {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 py-2 bg-[#1a1a1c] border border-[#232325] rounded-xl shadow-lg">
                <div className="px-4 py-2 border-b border-[#232325]">
                    <p className="text-sm text-gray-400">Signed in as</p>
                    <p className="text-sm font-medium text-white truncate">Super Admin</p>
                </div>
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-[#232325]"
                >
                    <LogOut size={16} />
                    <span>Sign out</span>
                </button>
                </div>
            )}
            </div>
        </div>
        </div>

        {/* Notifications Sidebar */}
        {showNotifications && (
          <div ref={notificationRef} className="fixed right-0 top-[10vh] h-[90vh] w-96 bg-[#0D0D0E] border-l border-[#232325] shadow-2xl z-50 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[#232325] flex items-center justify-between">
              <h2 className="text-white font-bold text-lg">Notifications</h2>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <button onClick={markAllAsRead} className="text-xs text-gray-400 hover:text-white">Mark all as read</button>
                )}
                <button onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <Bell size={48} className="mb-4 opacity-50" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div className="p-2">
                  {notifications.map((notification) => {
                    const { Icon, color, bgColor } = getNotificationIconAndColor(notification.type, notification.source);
                    return (
                      <div key={notification.id} className="mb-2 p-3 bg-[#1a1a1c] border border-[#232325] rounded-lg hover:bg-[#1f1f21]">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${bgColor} flex-shrink-0`}>
                            <Icon size={16} className={color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <h3 className="text-white font-medium text-sm">{notification.message}</h3>
                              <button onClick={() => dismissNotification(notification.id)} className="text-gray-400 hover:text-white">
                                <X size={14} />
                              </button>
                            </div>
                            <p className="text-gray-500 text-xs mt-2">{notification.timestamp}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {showNotifications && <div className="fixed inset-0 backdrop-blur-sm bg-opacity-20 z-30" onClick={() => setShowNotifications(false)} />}

        {renderContent()}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
